/**
 * Locale-aware display formatting for numbers, percentages, dates and durations.
 *
 * Every helper takes the Lingui locale string (`i18n.locale`, e.g. `de`) and maps it
 * through `getLocaleTag`, the one place that decides which regional variant a UI
 * language renders in. The product is a Swiss site, so the languages of Switzerland
 * render in their Swiss variant (`de` -> `de-CH`: `1'234.5`), English renders as
 * `en-CH` (day before month), and `pt`/`es` render as Portugal and Spain.
 *
 * Numeric dates are `dd.MM.yyyy` in every UI language: the dates belong to a Swiss
 * organisation, so a Portuguese-speaking employee reads the same `04.10.2026` the
 * office prints on its letters.
 *
 * Helpers return "" for missing or invalid input; the empty placeholder ("—") is a
 * presentation choice of the caller. Money lives in `lib/money.ts`.
 */

import { format, isValid, parseISO } from 'date-fns';
import type { Locale as DateFnsLocale } from 'date-fns';
import { de, enGB, es, fr, it, pt } from 'date-fns/locale';
import { formatInTimeZone } from 'date-fns-tz';
import { getOrgTimezone, toOrgDateOnly } from '@/utils/dateUtils';

const LOCALE_TAGS: Record<string, string> = {
  de: 'de-CH',
  en: 'en-CH',
  fr: 'fr-CH',
  it: 'it-CH',
  pt: 'pt-PT',
  es: 'es-ES',
};

const FALLBACK_LOCALE_TAG = 'en-CH';

// enGB, not enUS: English dates read day first ("4 Oct 2026") like every other UI language.
const DATE_FNS_LOCALES: Record<string, DateFnsLocale> = { de, en: enGB, fr, it, pt, es };

/** Numeric date shown in every UI language. */
export const DATE_DISPLAY_PATTERN = 'dd.MM.yyyy';
/** Numeric date with a two-digit year, for tight spaces such as a period label. */
export const DATE_SHORT_DISPLAY_PATTERN = 'dd.MM.yy';
/** Numeric date and 24-hour time shown in every UI language. */
export const DATE_TIME_DISPLAY_PATTERN = 'dd.MM.yyyy HH:mm';

/**
 * Language part of a Lingui locale or BCP 47 tag: `de`, `de-DE` and `de_CH` all give `de`.
 * Callers still pass full tags in places, and they must land on the same variant.
 */
function baseLanguage(locale: string | null | undefined): string {
  return (locale ?? '').split(/[-_]/)[0].toLowerCase();
}

/**
 * The BCP 47 tag `Intl` formats a UI language in.
 *
 * @example getLocaleTag('de') // 'de-CH'
 * @example getLocaleTag('pt') // 'pt-PT'
 * @example getLocaleTag('xx') // 'en-CH'
 */
export function getLocaleTag(locale: string | null | undefined): string {
  return LOCALE_TAGS[baseLanguage(locale)] ?? FALLBACK_LOCALE_TAG;
}

/**
 * A list of names joined the way the language joins them ("Anna, Beat und Clara").
 *
 * @example formatList(['Anna', 'Beat', 'Clara'], 'de') // "Anna, Beat und Clara"
 */
export function formatList(items: readonly string[], locale: string | null | undefined): string {
  return new Intl.ListFormat(getLocaleTag(locale), { style: 'long', type: 'conjunction' }).format(items);
}

/** The date-fns locale for a UI language (`en` uses British English, unknown falls back to it). */
export function getDateFnsLocale(locale: string | null | undefined): DateFnsLocale {
  return DATE_FNS_LOCALES[baseLanguage(locale)] ?? enGB;
}

/** The date-fns locale map keyed by Lingui locale. */
export const dateFnsLocales: Readonly<Record<string, DateFnsLocale>> = DATE_FNS_LOCALES;

// Building an Intl.NumberFormat is far slower than using one; table cells format thousands of values.
const numberFormatCache = new Map<string, Intl.NumberFormat>();

function getNumberFormat(tag: string, options: Intl.NumberFormatOptions): Intl.NumberFormat {
  const key = `${tag}|${JSON.stringify(options)}`;
  let formatter = numberFormatCache.get(key);
  if (!formatter) {
    formatter = new Intl.NumberFormat(tag, options);
    numberFormatCache.set(key, formatter);
  }
  return formatter;
}

function isFiniteNumber(value: number | null | undefined): value is number {
  return typeof value === 'number' && Number.isFinite(value);
}

/**
 * A number with the locale's grouping and decimal separator.
 *
 * @example formatNumber(1234.5, 'de') // "1'234.5"
 * @example formatNumber(1234.5, 'fr') // "1 234,5" (narrow no-break space)
 */
export function formatNumber(
  value: number | null | undefined,
  locale: string,
  options: Intl.NumberFormatOptions = {},
): string {
  if (!isFiniteNumber(value)) return '';
  const tag = getLocaleTag(locale);
  const formatter = getNumberFormat(tag, options);
  const group = GROUP_SEPARATORS[tag];
  if (!group) return formatter.format(value);
  return formatter
    .formatToParts(value)
    .map((part) => (part.type === 'group' ? group : part.value))
    .join('');
}

/**
 * The grouping separator of the Swiss variants, pinned because ICU data moves
 * under us: newer runtimes group de-CH with the typographic apostrophe U+2019
 * (older ones with the ASCII one), and CLDR 48 (Node 24.20+) switched fr-CH from
 * the narrow no-break space to the apostrophe. Money and the invoice PDFs use the
 * ASCII apostrophe, so the apostrophe languages do too; French keeps its space.
 */
const GROUP_SEPARATORS: Record<string, string> = {
  'de-CH': "'",
  'en-CH': "'",
  'it-CH': "'",
  'fr-CH': '\u202f',
};

/**
 * A number with exactly `digits` decimals, for values that line up in a column.
 *
 * @example formatDecimal(11.4, 'de', 1) // "11.4"
 * @example formatDecimal(16, 'de', 1) // "16.0"
 */
export function formatDecimal(value: number | null | undefined, locale: string, digits: number): string {
  return formatNumber(value, locale, { minimumFractionDigits: digits, maximumFractionDigits: digits });
}

/**
 * A ratio (0..1) as a percentage with exactly `digits` decimals, spaced the locale's way.
 *
 * @example formatPercent(0.853, 'de') // "85.3%"
 * @example formatPercent(0.853, 'es') // "85,3 %" (no-break space)
 */
export function formatPercent(value01: number | null | undefined, locale: string, digits = 1): string {
  return formatNumber(value01, locale, {
    style: 'percent',
    minimumFractionDigits: digits,
    maximumFractionDigits: digits,
  });
}

type DateInput = Date | string | number | null | undefined;

/**
 * The calendar day a value names. A `yyyy-MM-dd` string is taken as written, an ISO
 * instant as its day in the org zone, and a `Date` or epoch number as its local day
 * (a DatePicker builds `Date`s at local midnight).
 */
function toCalendarDate(value: DateInput): Date | null {
  if (value === null || value === undefined || value === '') return null;
  if (typeof value === 'string') {
    const parsed = parseISO(toOrgDateOnly(value));
    return isValid(parsed) ? parsed : null;
  }
  const date = typeof value === 'number' ? new Date(value) : value;
  return isValid(date) ? date : null;
}

function toInstant(value: DateInput): Date | null {
  if (value === null || value === undefined || value === '') return null;
  const date = typeof value === 'string' ? parseISO(value) : typeof value === 'number' ? new Date(value) : value;
  return isValid(date) ? date : null;
}

/**
 * Numeric date, `dd.MM.yyyy` in every locale. The locale is accepted so call sites
 * read like the other helpers and keep working if the convention ever varies.
 *
 * @example formatDateDisplay('2026-10-04', 'en') // "04.10.2026"
 */
export function formatDateDisplay(date: DateInput, _locale?: string): string {
  const day = toCalendarDate(date);
  return day ? format(day, DATE_DISPLAY_PATTERN) : '';
}

/**
 * Numeric date and 24-hour time of an instant, in the org timezone (like `formatOrgTime`),
 * so a browser in another zone shows the time the Kita recorded.
 *
 * @example formatDateTime('2026-10-04T05:30:00Z', 'de') // "04.10.2026 07:30"
 */
export function formatDateTime(date: DateInput, _locale?: string): string {
  const instant = toInstant(date);
  return instant ? formatInTimeZone(instant, getOrgTimezone(), DATE_TIME_DISPLAY_PATTERN) : '';
}

/**
 * Date with the month written out (date-fns `PPP`).
 *
 * @example formatDateLong('2026-10-04', 'de') // "4. Oktober 2026"
 * @example formatDateLong('2026-10-04', 'en') // "4 October 2026"
 */
export function formatDateLong(date: DateInput, locale: string): string {
  const day = toCalendarDate(date);
  return day ? format(day, 'PPP', { locale: getDateFnsLocale(locale) }) : '';
}

/**
 * Date with the month abbreviated (date-fns `PP`), for headers and compact cells.
 *
 * @example formatDateMedium('2026-10-04', 'de') // "4. Okt. 2026"
 * @example formatDateMedium('2026-10-04', 'en') // "4 Oct 2026"
 */
export function formatDateMedium(date: DateInput, locale: string): string {
  const day = toCalendarDate(date);
  return day ? format(day, 'PP', { locale: getDateFnsLocale(locale) }) : '';
}

/**
 * Weekday name of a date.
 *
 * @example formatWeekday('2026-10-04', 'de') // "Sonntag"
 * @example formatWeekday('2026-10-04', 'de', 'short') // "So."
 */
export function formatWeekday(date: DateInput, locale: string, width: 'long' | 'short' = 'long'): string {
  const day = toCalendarDate(date);
  return day ? format(day, width === 'long' ? 'EEEE' : 'EEE', { locale: getDateFnsLocale(locale) }) : '';
}

const MONTH_PERIOD = /^(\d{4})-(\d{2})(?:-\d{2})?$/;

/**
 * Month name and year of a date or a month period (`yyyy-MM`, or `yyyy-MM-dd` as the
 * backend sends `period_month`). A period is pinned to its 1st so no zone shifts it.
 * `long` writes the month out, `short` abbreviates month and year for chart axes,
 * `medium` abbreviates the month and keeps the four-digit year.
 *
 * @example formatMonthYear('2026-10', 'de') // "Oktober 2026"
 * @example formatMonthYear('2026-10-01', 'en') // "October 2026"
 * @example formatMonthYear('2026-01', 'fr', 'short') // "janv. 26"
 * @example formatMonthYear('2025-11', 'en', 'medium') // "Nov 2025"
 */
export function formatMonthYear(
  periodOrDate: DateInput,
  locale: string,
  style: 'long' | 'medium' | 'short' = 'long',
): string {
  const period = typeof periodOrDate === 'string' ? MONTH_PERIOD.exec(periodOrDate) : null;
  const month = period ? new Date(Number(period[1]), Number(period[2]) - 1, 1) : toCalendarDate(periodOrDate);
  if (!month || !isValid(month)) return '';
  const pattern = style === 'long' ? 'LLLL yyyy' : style === 'medium' ? 'LLL yyyy' : 'LLL yy';
  return format(month, pattern, { locale: getDateFnsLocale(locale) });
}

export interface DateRangeFormatOptions {
  /**
   * `numeric` (default) uses `dd.MM.yyyy`, `short` uses `dd.MM.yy`, `medium` the
   * abbreviated month of `formatDateMedium`.
   */
  style?: 'numeric' | 'short' | 'medium';
}

/**
 * An inclusive date range joined by an en dash; a single-day range shows one date.
 * Returns "" when either end is missing, because an open range needs a sentence
 * ("from …"), which belongs in a translated message.
 *
 * @example formatDateRange('2026-10-04', '2026-10-10', 'de') // "04.10.2026 – 10.10.2026"
 * @example formatDateRange('2026-10-04', '2026-10-10', 'de', { style: 'short' }) // "04.10.26 – 10.10.26"
 * @example formatDateRange('2026-10-04', '2026-10-10', 'en', { style: 'medium' }) // "4 Oct 2026 – 10 Oct 2026"
 */
export function formatDateRange(
  from: DateInput,
  to: DateInput,
  locale: string,
  options: DateRangeFormatOptions = {},
): string {
  const start = toCalendarDate(from);
  const end = toCalendarDate(to);
  if (!start || !end) return '';
  const formatOne =
    options.style === 'medium'
      ? formatDateMedium
      : options.style === 'short'
        ? (day: Date) => format(day, DATE_SHORT_DISPLAY_PATTERN)
        : formatDateDisplay;
  const startText = formatOne(start, locale);
  const endText = formatOne(end, locale);
  return startText === endText ? startText : `${startText} – ${endText}`;
}

const NBSP = ' ';

/**
 * A duration in whole minutes as hours and minutes. `h` and `min` are unit symbols
 * shared by all six UI languages; a no-break space keeps each value with its unit.
 *
 * @example formatDuration(450, 'de') // "7 h 30 min"
 * @example formatDuration(45, 'de') // "45 min"
 * @example formatDuration(480, 'de') // "8 h"
 */
export function formatDuration(minutes: number | null | undefined, locale: string): string {
  if (!isFiniteNumber(minutes)) return '';
  const total = Math.round(Math.abs(minutes));
  const sign = minutes < 0 && total > 0 ? '-' : '';
  const hours = Math.floor(total / 60);
  const rest = total % 60;
  const parts: string[] = [];
  if (hours > 0) parts.push(`${formatNumber(hours, locale)}${NBSP}h`);
  if (rest > 0 || hours === 0) parts.push(`${rest}${NBSP}min`);
  return sign + parts.join(' ');
}
